// src/providers/UserProvider.tsx
// ============================================================================
// USER PROVIDER — Enterprise Stable (2026) — Secure Level 10
// ============================================================================
// Guarantees:
// ✅ Single source of truth for auth/session/user/profile state
// ✅ StrictMode-safe (no double init storms)
// ✅ No signOut storms (re-entrancy guarded + reasoned signOut)
// ✅ Never logs JWT/session; DEV logs are minimal + deduped
// ✅ Token refresh does NOT wipe profile/user state
// ✅ Profile fetch is cancelable via request epoch
// ✅ Force-logout + SessionManager + Idle timeout are coordinated
// ✅ Security teardown ALWAYS happens before any signOut
// ✅ resetPassword / updatePassword / refreshSession route through authAPI
// ✅ signInWithGoogle delegates to authAPI (Supabase handles the OAuth redirect)
// ✅ signUp accepts optional name — stored as user_metadata.full_name
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AuthChangeEvent, Session, User as SupabaseUser } from '@supabase/supabase-js';

import { supabase } from '@/lib/supabase/supabaseClient';
import { UserContext } from '@/contexts/UserContext';
import type { AppUser, UserContextValue, UserRole } from '@/contexts/userTypes';

import { SessionManager } from '@/security/SessionManager';
import { ActivityTracker } from '@/security/ActivityTracker';
import { subscribeToForceLogout } from '@/security/ForceLogoutListener';

import { mapSupabaseUser } from '@/utils/mapSupabaseUserToAppUser';
import { getMyProfile, updateMyProfile } from '@/lib/supabase/db/profile.api';
import type { Profile } from '@/types/profile';
import { authAPI } from '@/features/auth/auth.api';

import { loadProfileCache, saveProfileCache, clearProfileCache } from '@/lib/cache/profileCache';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserProviderProps {
  children: ReactNode;
}

type SignOutReason =
  | 'manual'
  | 'session_expired'
  | 'idle_timeout'
  | 'admin_forced_logout'
  | 'auth_forbidden'
  | 'auth_invalid'
  | 'unknown';

type ApplyContext = {
  event?: AuthChangeEvent;
  boot?: boolean;
};

// ─── Dev logging (deduped, low-noise) ────────────────────────────────────────

function devLog(msg: string, meta?: Record<string, unknown>) {
  if (!import.meta.env.DEV) return;
  console.log(`🔐 [AUTH] ${msg}`, meta ?? '');
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function mergeUser(authUser: SupabaseUser | null, profile: Profile | null): AppUser | null {
  const base = mapSupabaseUser(authUser);
  if (!base) return null;
  return {
    ...base,
    name: profile?.full_name ?? base.name,
    phone: profile?.phone ?? base.phone ?? null,
    role: (profile?.role ?? base.role ?? 'customer') as AppUser['role'],
  };
}

// Events that imply a new user identity (hydrate cache, refresh profile)
const SIGN_IN_EVENTS = new Set<AuthChangeEvent>(['SIGNED_IN', 'USER_UPDATED']);
// Events that are session-churn only (do NOT touch profile/user)
const SESSION_ONLY_EVENTS = new Set<AuthChangeEvent>(['TOKEN_REFRESHED']);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function UserProvider({ children }: UserProviderProps) {
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // ── Coordination refs ──────────────────────────────────────────────────────
  const profileEpochRef = useRef(0);
  const signingOutRef = useRef(false);
  const initializedRef = useRef(false);
  const applyRef = useRef<(u: SupabaseUser | null, s: Session | null, ctx?: ApplyContext) => void>(
    () => {},
  );
  const sessionManagerRef = useRef<SessionManager | null>(null);
  const activityRef = useRef<ActivityTracker | null>(null);
  const forceLogoutCleanupRef = useRef<(() => void) | null>(null);

  // ── Security teardown ──────────────────────────────────────────────────────

  const teardownSecurity = useCallback(() => {
    try {
      sessionManagerRef.current?.stop();
    } catch {
      /* ignore */
    }
    try {
      activityRef.current?.stop();
    } catch {
      /* ignore */
    }
    try {
      forceLogoutCleanupRef.current?.();
    } catch {
      /* ignore */
    }
    forceLogoutCleanupRef.current = null;
  }, []);

  // ── Safe sign out (re-entrancy guarded) ───────────────────────────────────

  const safeSignOut = useCallback(
    async (reason: SignOutReason) => {
      if (signingOutRef.current) return;
      signingOutRef.current = true;

      try {
        devLog('signOut start', { reason });
        teardownSecurity();
        clearProfileCache();
        // scope:'local' clears only the local session — no server redirect,
        // no browser navigation from Supabase. This prevents the 404 flash
        // that happens when Supabase's global signOut redirects the browser
        // to a URL that isn't a registered route.
        await supabase.auth.signOut({ scope: 'local' });
        setSupabaseUser(null);
        setSession(null);
        setUser(null);
        setProfile(null);
      } catch {
        // Never throw — user intent is to be signed out.
      } finally {
        signingOutRef.current = false;
        devLog('signOut end', { reason });
      }
    },
    [teardownSecurity],
  );

  // ── Profile management (epoch-cancelable) ─────────────────────────────────

  const fetchProfileSafe = useCallback(async (authUser: SupabaseUser) => {
    const myEpoch = ++profileEpochRef.current;
    try {
      const p = await getMyProfile(authUser.id);
      if (myEpoch !== profileEpochRef.current) return;
      setProfile(p);
      setUser(mergeUser(authUser, p));
      saveProfileCache(p);
    } catch {
      if (myEpoch !== profileEpochRef.current) return;
      setProfile(null);
      setUser(mergeUser(authUser, null));
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!supabaseUser) return;
    await fetchProfileSafe(supabaseUser);
  }, [supabaseUser, fetchProfileSafe]);

  const updateProfileAction = useCallback(
    async (input: Pick<Profile, 'full_name' | 'phone'>): Promise<Profile> => {
      if (!supabaseUser) throw new Error('Not authenticated');
      const updated = await updateMyProfile(supabaseUser.id, input);
      setProfile(updated);
      setUser(mergeUser(supabaseUser, updated));
      saveProfileCache(updated);
      return updated;
    },
    [supabaseUser],
  );

  // ── Apply user (deterministic state machine) ──────────────────────────────

  const applyUser = useCallback(
    (u: SupabaseUser | null, s: Session | null, ctx?: ApplyContext) => {
      profileEpochRef.current++;

      const event = ctx?.event;
      const boot = Boolean(ctx?.boot);

      setSupabaseUser(u);
      setSession(s);

      if (!u) {
        setUser(null);
        setProfile(null);
        clearProfileCache();
        teardownSecurity();
        if (!boot) devLog('applied signed_out');
        return;
      }

      if (event && SESSION_ONLY_EVENTS.has(event)) {
        if (s) sessionManagerRef.current?.start(s);
        devLog('session_only_event', { event, uid: u.id });
        return;
      }

      setUser(mergeUser(u, null));

      if (boot || !event || SIGN_IN_EVENTS.has(event)) {
        const cached = loadProfileCache();
        if (cached) {
          setProfile(cached);
          setUser(mergeUser(u, cached));
        }
      }

      void fetchProfileSafe(u);

      if (s) sessionManagerRef.current?.start(s);

      try {
        forceLogoutCleanupRef.current?.();
      } catch {
        /* ignore */
      }
      forceLogoutCleanupRef.current = subscribeToForceLogout(u.id, () => {
        void safeSignOut('admin_forced_logout');
      });

      devLog('applied', { uid: u.id, event: event ?? 'init' });
    },
    [fetchProfileSafe, teardownSecurity, safeSignOut],
  );

  useEffect(() => {
    applyRef.current = applyUser;
  }, [applyUser]);

  // ── Security systems init (StrictMode-safe) ───────────────────────────────

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    sessionManagerRef.current = new SessionManager({
      onExpire: () => void safeSignOut('session_expired'),
      onRefresh: (newSession) => {
        setSession(newSession);
        sessionManagerRef.current?.start(newSession);
        devLog('session_refreshed');
      },
    });

    activityRef.current = new ActivityTracker(60, () => void safeSignOut('idle_timeout'));
    activityRef.current.start();

    return () => {
      teardownSecurity();
    };
  }, [safeSignOut, teardownSecurity]);

  // ── Auth bootstrap + listener ──────────────────────────────────────────────

  useEffect(() => {
    let mounted = true;

    const boot = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        applyRef.current(data.session?.user ?? null, data.session, {
          event: 'INITIAL_SESSION',
          boot: true,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void boot();

    const { data: sub } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;
      if (event === 'INITIAL_SESSION') return;
      if (event === 'SIGNED_OUT') {
        applyRef.current(null, null, { event });
        return;
      }
      applyRef.current(nextSession?.user ?? null, nextSession, { event });
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // ── Auth actions (public API) ──────────────────────────────────────────────

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await authAPI.signIn({ email, password });
    if (error) throw error;
    const { data } = await supabase.auth.getSession();
    if (!data.session?.user) throw new Error('Session not established after sign-in');
  }, []);

  const signUp = useCallback(async (email: string, password: string, name?: string) => {
    // name is stored as user_metadata.full_name via authAPI.signUp's fullName field
    const { error } = await authAPI.signUp({ email, password, fullName: name });
    if (error) throw error;
  }, []);

const signInWithGoogle = useCallback(async (options?: { redirectPath?: string }) => {
  await authAPI.signInWithGoogle({
    redirectPath: options?.redirectPath ?? '/auth/callback',
  });
}, []);

  const signOut = useCallback(async () => {
    await safeSignOut('manual');
  }, [safeSignOut]);

  const resetPassword = useCallback(async (email: string, options?: { redirectTo?: string }) => {
    await authAPI.requestPasswordReset({ email, redirectPath: options?.redirectTo });
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    await authAPI.updatePassword({ password });
  }, []);

  const refreshSession = useCallback(async () => {
    await authAPI.refreshSessionState();
  }, []);

  const updateMetadata = useCallback(async (metadata: Record<string, unknown>) => {
    // Direct Supabase call: authAPI has no updateMetadata surface.
    // user_metadata is intentionally untyped — callers own the shape.
    const { error } = await supabase.auth.updateUser({ data: metadata });
    if (error) throw error;
  }, []);

  // ── Context value ──────────────────────────────────────────────────────────

  const role: UserRole = (user?.role as UserRole) ?? 'guest';
  const isAuthenticated: boolean = Boolean(user);
  const isAdmin: boolean = role === 'admin';

  const value = useMemo<UserContextValue>(
    () => ({
      user,
      supabaseUser,
      profile,
      session,
      loading,
      isAuthenticated,
      isAdmin,
      role,
      setUser,

      signIn,
      signUp,
      signInWithGoogle,
      signOut,

      resetPassword,
      updatePassword,
      refreshSession,
      updateMetadata,

      refreshProfile,
      updateProfile: updateProfileAction,
    }),
    [
      user,
      supabaseUser,
      profile,
      session,
      loading,
      isAuthenticated,
      isAdmin,
      role,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      resetPassword,
      updatePassword,
      refreshSession,
      updateMetadata,
      refreshProfile,
      updateProfileAction,
    ],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export default UserProvider; 